import React from 'react';

const invertAngle = angle => {
  return 90 - angle;
};

// Helper to render 3D polygon with thickness
const render3DPolygon = (vertices, thickness, fillColor, strokeColor) => {
  if (!vertices || vertices.length < 3) return null;
  
  // Calculate bounding box for SVG viewBox
  const minX = Math.min(...vertices.map(v => parseFloat(v.x || 0)));
  const maxX = Math.max(...vertices.map(v => parseFloat(v.x || 0)));
  const minY = Math.min(...vertices.map(v => parseFloat(v.y || 0)));
  const maxY = Math.max(...vertices.map(v => parseFloat(v.y || 0)));
  const minZ = Math.min(...vertices.map(v => parseFloat(v.z || 0)));
  const maxZ = Math.max(...vertices.map(v => parseFloat(v.z || 0)));
  
  const width = maxX - minX || 1;
  const height = maxY - minY || 1;
  const depth = maxZ - minZ || 0;
  
  // Create SVG points string (using x, y for front face)
  const pointsStr = vertices.map(v => `${parseFloat(v.x || 0) - minX},${parseFloat(v.y || 0) - minY}`).join(' ');
  
  return (
    <div style={{ position: 'relative', width: `${width}em`, height: `${height}em`, transformStyle: 'preserve-3d' }}>
      {/* Front face */}
      <svg
        style={{
          position: 'absolute',
          width: `${width}em`,
          height: `${height}em`,
          transform: `translateZ(${thickness / 2}em)`,
          overflow: 'visible',
        }}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        <polygon
          points={pointsStr}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth="0.02"
        />
      </svg>
      {/* Side edges - connect front and back faces */}
      {vertices.map((v, i) => {
        const next = vertices[(i + 1) % vertices.length];
        const x1 = parseFloat(v.x || 0) - minX;
        const y1 = parseFloat(v.y || 0) - minY;
        const x2 = parseFloat(next.x || 0) - minX;
        const y2 = parseFloat(next.y || 0) - minY;
        const edgeLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
        
        return (
          <div
            key={`edge-${i}`}
            style={{
              position: 'absolute',
              width: `${edgeLength}em`,
              height: `${thickness}em`,
              background: fillColor,
              border: `0.02em solid ${strokeColor}`,
              left: `${x1}em`,
              top: `${y1}em`,
              transformOrigin: '0 0',
              transform: `rotateZ(${angle}deg) rotateX(90deg) translateY(${-thickness / 2}em)`,
            }}
          />
        );
      })}
    </div>
  );
};

export class SideShadingDevice extends React.Component {
  render() {
    const {
      side,
      origin: { x, y, z },
      size: { width, height },
      vertices,
      isHighLight,
    } = this.props;
    
    const fillColor = isHighLight ? 'rgba(97, 255, 109, 0.6)' : 'rgba(255, 99, 71, 0.6)';
    const strokeColor = 'rgba(0,0,0,0.5)';
    const thickness = 0.1;
    
    // Render polygon if vertices exist
    if (vertices && vertices.length >= 3) {
      return (
        <div
          style={{
            position: 'absolute',
            transform: `rotateY(-90deg) translateX(${x}em) translateY(${z}em) translateZ(${-y}em)`,
            transformOrigin: 'left center',
            transformStyle: 'preserve-3d',
          }}
        >
          {render3DPolygon(vertices.map(v => ({ x: parseFloat(v.x || 0), y: parseFloat(v.y || 0), z: 0 })), thickness, fillColor, strokeColor)}
        </div>
      );
    }
    
    // Rectangle mode
    let classNames = ['sheet'];
    if (isHighLight) {
      classNames = [...classNames, 'active'];
    }
    classNames = [...classNames, `sheet-${side}`];

    return (
      <div
        className={classNames.join(' ')}
        style={{
          transform: `rotateY(-90deg) translateX(${x}em) translateY(${z}em) translateZ(${-y}em)`,
          width: `${width}em`,
          height: `${height}em`,
        }}
      >
        <div
          className="side front"
          style={{ width: `${width}em`, height: `${height}em` }}
        />
        <div
          className="side back"
          style={{ width: `${width}em`, height: `${height}em` }}
        />
        <div
          className="side side-width-thin right"
          style={{
            left: `calc((${width}em - 0.1em) / 2.0 )`,
            transform: `rotateY(90deg) translateZ(${width / 2}em)`,
            height: `${height}em`,
          }}
        />
        <div
          className="side side-width-thin left"
          style={{ height: `${height}em` }}
        />
        <div
          className="side side-height-thin top"
          style={{ width: `${width}em` }}
        />
        <div
          className="side side-height-thin bottom"
          style={{
            top: `calc((${height}em - 0.1em) / 2.0 )`,
            transform: `rotateX(-90deg) translateZ(${height * 0.5}em)`,
            width: `${width}em`,
          }}
        />
      </div>
    );
  }
}

export class FrontShadingDevice extends React.Component {
  render() {
    const {
      origin: { x, y, z },
      size: { width, height: length },
      xAngle,
      vertices,
      isHighLight,
    } = this.props;
    
    const fillColor = isHighLight ? 'rgba(97, 255, 109, 0.6)' : 'rgba(255, 99, 71, 0.6)';
    const strokeColor = 'rgba(0,0,0,0.5)';
    const thickness = 0.1;
    
    // Render polygon if vertices exist
    if (vertices && vertices.length >= 3) {
      return (
        <div
          style={{
            position: 'absolute',
            transform: `translateX(${y}em) translateY(${z}em) translateZ(${x}em) rotateX(${-invertAngle(xAngle)}deg)`,
            transformOrigin: 'top center',
            transformStyle: 'preserve-3d',
          }}
        >
          {render3DPolygon(vertices.map(v => ({ x: parseFloat(v.x || 0), y: parseFloat(v.y || 0), z: 0 })), thickness, fillColor, strokeColor)}
        </div>
      );
    }
    
    // Rectangle mode
    let classNames = ['sheet', 'sheet-front'];
    if (isHighLight) {
      classNames = [...classNames, 'active'];
    }

    return (
      <div
        className={classNames.join(' ')}
        style={{
          height: `${length}em`,
          width: `${width}em`,
          transform: `translateX(${y}em) translateY(${z}em) translateZ(${x}em) rotateX(${-invertAngle(xAngle)}deg)`
        }}
      >
        <div
          className="side front"
          style={{ height: `${length}em`, width: `${width}em` }}
        />
        <div
          className="side back"
          style={{ height: `${length}em`, width: `${width}em` }}
        />
        <div
          className="side side-width-thin right"
          style={{
            height: `${length}em`,
            left: `calc((${width}em - 0.1em) / 2.0 )`,
            transform: `rotateY(90deg) translateZ(${width / 2}em)`,
          }}
        />
        <div
          className="side side-width-thin left"
          style={{ height: `${length}em` }}
        />
        <div
          className="side side-height-thin top"
          style={{ width: `${width}em` }}
        />
        <div
          className="side side-height-thin bottom"
          style={{
            width: `${width}em`,
            top: `calc((${length}em - 0.1em) / 2.0 )`,
            transform: `rotateX(-90deg) translateZ(${length / 2}em)`,
          }}
        />
      </div>
    );
  }
}

export class TopShadingDevice extends React.Component {
  render() {
    const {
      origin: { x, y, z },
      size: { width, height: length },
      xAngle,
      vertices,
      isHighLight,
    } = this.props;
    
    const fillColor = isHighLight ? 'rgba(97, 255, 109, 0.6)' : 'rgba(255, 99, 71, 0.6)';
    const strokeColor = 'rgba(0,0,0,0.5)';
    const thickness = 0.1;
    
    // Render polygon if vertices exist
    if (vertices && vertices.length >= 3) {
      return (
        <div
          style={{
            position: 'absolute',
            transform: `translateX(${y}em) translateY(${z}em) translateZ(${x}em) rotateX(${invertAngle(-xAngle)}deg)`,
            transformOrigin: 'top center',
            transformStyle: 'preserve-3d',
          }}
        >
          {render3DPolygon(vertices.map(v => ({ x: parseFloat(v.x || 0), y: parseFloat(v.y || 0), z: 0 })), thickness, fillColor, strokeColor)}
        </div>
      );
    }
    
    // Rectangle mode
    let classNames = ['sheet', 'sheet-top'];
    if (isHighLight) {
      classNames = [...classNames, 'active'];
    }

    return (
      <div
        className={classNames.join(' ')}
        style={{
          height: `${length}em`,
          width: `${width}em`,
          transform: `translateX(${y}em) translateY(${z}em) translateZ(${x}em) rotateX(${invertAngle(-xAngle)}deg)`
        }}
      >
        <div
          className="side front"
          style={{ height: `${length}em`, width: `${width}em` }}
        />
        <div
          className="side back"
          style={{ height: `${length}em`, width: `${width}em` }}
        />
        <div
          className="side side-width-thin right"
          style={{
            height: `${length}em`,
            left: `calc((${width}em - 0.1em) / 2.0 )`,
            transform: `rotateY(90deg) translateZ(${width / 2}em)`,
          }}
        />
        <div
          className="side side-width-thin left"
          style={{ height: `${length}em` }}
        />
        <div
          className="side side-height-thin top"
          style={{ width: `${width}em` }}
        />
        <div
          className="side side-height-thin bottom"
          style={{
            width: `${width}em`,
            top: `calc((${length}em - 0.1em) / 2.0 )`,
            transform: `rotateX(-90deg) translateZ(${length / 2}em)`,
          }}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/sc_editor/ShadingDevices.js